import {Injectable, signal, WritableSignal} from '@angular/core';
import {BarSeriesOption, EChartsOption, GaugeSeriesOption, LineSeriesOption} from 'echarts';
import { ChartColors, ChartDataTransformers } from '../../common/general-types/chart.types';

/** Optional color breakpoints for the ring */
export type GaugeThreshold = {
  /** Value (absolute, same units as min/max) at which color changes */
  value: number;
  /** Color up to this threshold. If omitted, falls back to themed progress color. */
  color?: string;
};

export type RingGaugeParams = {
  name?: string;
  value: number;
  min?: number;
  max?: number;
  unit?: string;
  ringWidth?: number;
  trackColor?: string;
  detailColor?: string;
  progressColor?: string;
  thresholds?: GaugeThreshold[];
};

@Injectable({
  providedIn: 'root'
})
export class ChartsService {

  chartColorsLightTheme: ChartColors = {
    xAxis: {
      xAxis: '#364153',
      xAxisLabel: '#372aac'
    },
    yAxis: {
      yAxis: '#364153',
      yAxisLabel: '#372aac'
    },
    tooltip: {
      background: '#101828',
      border: '#101828',
      text: '#f3f4f6',
    },
    series: {
      marker: {
        border: '#372aac',
        background: '#c6d2ff'
      },
      line: '#90a1b9',
      label: '#372aac'
    },
  }

  chartColorsDarkTheme: ChartColors = {
    xAxis: {
      xAxis: '#d1d5dc',
      xAxisLabel: '#9ca3af'
    },
    yAxis: {
      yAxis: '#d1d5dc',
      yAxisLabel: '#9ca3af'
    },
    tooltip: {
      background: '#101828',
      border: '#101828',
      text: '#f3f4f6',
    },
    series: {
      marker: {
        border: '#6366f1',
        background: '#c6d2ff'
      },
      line: '#a3b3ff',
      label: '#a3b3ff'
    },
  }

  chartTheme: WritableSignal<ChartColors> = signal(this.chartColorsLightTheme);

  constructor() { }

  generateChartOptionFromCount<T>(
    rawData: T[],
    transformers: ChartDataTransformers<T>,
    type: 'line' | 'bar' = 'bar'  // allow switching chart types
  ): EChartsOption {
    const xMap = new Map<string | number, number>();

    rawData.forEach(item => {
      const x = transformers.getX(item);
      const y = transformers.getY(item);
      xMap.set(x, (xMap.get(x) ?? 0) + y); // Aggregation logic
    });

    const sortedX = Array.from(xMap.keys()).sort();
    const yValues = sortedX.map(x => xMap.get(x) ?? 0);

    return {
      xAxis: {
        type: 'category',
        data: sortedX,
      },
      yAxis: {
        type: 'value',
      },
      series: [
        {
          data: yValues,
          type,
          label: {
            show: true,
            position: 'top',
          },
          itemStyle: {
            color: '#4f39f6',
            borderRadius: [6, 6, 0, 0],
          }
        }
      ],
      tooltip: {
        trigger: 'axis',
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      }
    };
  }

  generateChartOption<T>(
    rawData: T[],
    transformers: ChartDataTransformers<T>,
    type: 'line' | 'bar' = 'bar'
  ): echarts.EChartsOption {
    const xMap = new Map<string | number, number>();

    rawData.forEach(item => {
      const x = transformers.getX(item);
      const y = transformers.getY(item);
      xMap.set(x, (xMap.get(x) ?? 0) + y);
    });

    const sortedX = Array.from(xMap.keys()).sort();
    const yValues = sortedX.map(x => xMap.get(x) ?? 0);

    return {
      xAxis: {
        type: 'category',
        data: sortedX,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          show: true,
          color: '#9ca3af',
          fontSize: 12
        }
      } as echarts.XAXisComponentOption,

      yAxis: {
        type: 'value',
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          show: true,
          color: '#9ca3af',
          formatter: (value: number) => `£${value.toLocaleString()}`
        }
      } as echarts.YAXisComponentOption,

      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const p = params[0];
          return `£${Number(p.value).toLocaleString()}`;
        }
      },

      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        containLabel: true
      },

      series: [
        {
          data: yValues,
          type,
          ...(type === 'line'
            ? {
              smooth: true,
              symbol: 'circle',
              symbolSize: 10,
              lineStyle: {
                width: 3,
                color: '#7f9cf5'
              }
            }
            : {
              // Bar-specific fallback
              barWidth: '50%',
            }),
          itemStyle: {
            color: '#6366f1',
            borderWidth: 2,
            borderColor: '#0e1323'
          },
          label: {
            show: true,
            position: 'top',
            formatter: (params: any) => `£${Number(params.value).toLocaleString()}`,
            color: '#dbeafe',
            fontSize: 14,
            fontWeight: 'bold'
          },
          emphasis: {
            scale: true
          }
        } as LineSeriesOption | BarSeriesOption
      ]

    };
  }

  /**
   * Themed ring gauge (full-circle). Clean KPI style (no ticks/labels),
   * rounded caps, animated value, optional thresholds for multi-color progress.
   */
  generateRingGaugeOption(params: RingGaugeParams): EChartsOption {
    const theme = this.chartTheme(); // use current theme signal

    const {
      name = 'Gauge',
      value,
      min = 0,
      max = 100,
      unit = '',
      ringWidth = 18,
      // sensible themed defaults
      trackColor = theme.series?.marker?.border ?? '#e5e7eb',
      detailColor = theme.series?.label ?? '#111827',
      progressColor = theme.series?.line ?? theme.series?.marker?.background ?? '#3b82f6',
      thresholds,
    } = params;

    const span = Math.max(max - min, 1);
    const clamp = (n: number) => Math.min(max, Math.max(min, n));

    // ECharts expects [stop(0..1), color] tuples for axisLine.color
    const normalizedStops: [number, string][] =
      thresholds && thresholds.length
        ? thresholds
          .sort((a, b) => a.value - b.value)
          .map(t => [
            Math.min(1, Math.max(0, (t.value - min) / span)),
            t.color ?? progressColor,
          ])
        : [[1, progressColor]];

    const gauge: GaugeSeriesOption = {
      type: 'gauge',
      startAngle: 90,
      endAngle: -270,           // full ring
      min,
      max,
      radius: '90%',
      pointer: { show: false },
      progress: {
        show: true,
        width: ringWidth,
        roundCap: true,
        // With multi-color ranges use 'auto' so progress follows axisLine bands
        itemStyle: { color: thresholds?.length ? 'auto' : progressColor },
      },
      axisLine: {
        roundCap: true,
        lineStyle: {
          width: ringWidth,
          color: [
            ...normalizedStops,
            [1, trackColor], // remaining track to 100%
          ],
        },
      },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
      anchor: { show: false },
      title: {
        show: !!name,
        offsetCenter: [0, '48%'],
        fontSize: 12,
      },
      detail: {
        valueAnimation: true,
        offsetCenter: [0, 0],
        formatter: (val: number) => `${val}${unit}`,
        fontSize: 24,
        fontWeight: 700,
        color: detailColor,
      },
      data: [{ value: clamp(value), name }],
    };

    return {
      series: [gauge],
      tooltip: {
        trigger: 'item',
        backgroundColor: theme.tooltip.background,
        borderColor: theme.tooltip.border,
        textStyle: {color: theme.tooltip.text},
        formatter: (p: any) => `${p.seriesName || name}: <b>${p.value}${unit}</b>`,
      },
    };
  }
}
